'use client';

import * as React from 'react';
import type { IPaginationResponse } from '@/api/resort/types';
import { REGION_RESORT_PAGE_SIZE } from '@/ots-constants';
import { Diagnostics } from '@/util/diagnostics';

// Region list endpoints serve one page at a time and clamp `limit` at 100, so
// a big region (476/Europe carries 417 active resorts, 409 of them in the snow
// report's closed bucket alone) can never arrive in a single response. The
// server renders page 0; this hook fetches the rest on demand.
//
// Two callers, two shapes:
//
//   loadMore()  one more page, for the Load More button.
//   loadAll()   every remaining page at once, for sorting. The tables sort
//               client-side over the rows they hold, so sorting a partial set
//               ranks 100 of 409 and presents the winner as the region's —
//               correct only once everything is in.
//
// `total` comes from the server's pagination.count, which describes the whole
// set rather than the delivered slice, so it stays authoritative no matter how
// many pages are loaded.

export type Paginated<T> = {
  data?: T[];
  pagination?: IPaginationResponse;
};

// Guards a runaway loop if `count` and the delivered pages ever disagree.
// 20 pages is 2000 rows — an order of magnitude beyond the largest region.
const MAX_PAGES = 20;

type Args<T> = {
  /** The server-rendered first page. */
  initial: Paginated<T> | null | undefined;
  /** Fetches one page by index. Keeps the hook agnostic of which endpoint. */
  fetchPage: (page: number) => Promise<Paginated<T>>;
  /** Identifies the list in diagnostics, e.g. `region 476 skireport closed`. */
  label: string;
};

export type LoadMoreResorts<T> = {
  rows: T[];
  /** Rows in the whole set, not the loaded slice. */
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  failed: boolean;
  loadMore: () => void;
  /** Pulls every remaining page, so a client-side sort ranks the full set. */
  loadAll: () => void;
};

export const useLoadMoreResorts = <T >({
  initial, fetchPage, label,
}: Args<T>): LoadMoreResorts<T> => {
  const seed = React.useMemo(() => initial?.data ?? [], [initial]);
  const total = initial?.pagination?.count ?? seed.length;

  const [rows, setRows] = React.useState<T[]>(seed);
  const [isLoading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  // Bumped on every reset and every request, so a response that lands after
  // something newer started is dropped instead of appended out of order.
  const token = React.useRef(0);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  // A fresh server payload — navigation, or a revalidated render — restarts
  // from page 0. Keyed on the payload's shape rather than object identity,
  // which changes on every render and would reset mid-interaction.
  const seedKey = `${label}|${total}|${seed.length}`;
  React.useEffect(() => {
    token.current += 1;
    setRows(seed);
    setLoading(false);
    setFailed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  const load = React.useCallback(async (mode: 'next' | 'all') => {
    if (isLoading) return;

    const loaded = rowsRef.current.length;
    if (loaded >= total) return;

    const mine = token.current + 1;
    token.current = mine;

    const lastPage = Math.ceil(total / REGION_RESORT_PAGE_SIZE) - 1;
    const from = Math.floor(loaded / REGION_RESORT_PAGE_SIZE);
    const to = mode === 'next' ? from : Math.min(lastPage, from + MAX_PAGES - 1);

    const pages: number[] = [];
    for (let p = from; p <= to; p += 1) pages.push(p);

    // The only path left that can leave rows unreachable. Every truncation this
    // codebase had was silent, so this one says so rather than stopping quietly.
    if (mode === 'all' && to < lastPage) {
      Diagnostics.error({
        tag: 'region-load-more',
        message: `${label}: ${total} rows exceeds the ${MAX_PAGES}-page ceiling; sorting covers only the first ${(to + 1) * REGION_RESORT_PAGE_SIZE}`,
        data: { label, total, maxPages: MAX_PAGES },
      });
    }

    setLoading(true);
    setFailed(false);

    try {
      // `total` is known up front, so the remaining pages go out together
      // rather than chained — one round-trip's latency instead of five.
      const responses = await Promise.all(pages.map((page) => fetchPage(page)));
      if (token.current !== mine) return;

      const batch = responses.flatMap((r) => r?.data ?? []);
      if (batch.length > 0) setRows((prev) => [...prev, ...batch]);
    } catch (error) {
      if (token.current !== mine) return;
      Diagnostics.error({
        tag: 'region-load-more',
        message: `${label}: failed to load page${pages.length > 1 ? 's' : ''} ${pages.join(', ')}`,
        error: error as Error,
        data: {
          label, pages, loaded, total,
        },
      });
      setFailed(true);
    } finally {
      if (token.current === mine) setLoading(false);
    }
  }, [fetchPage, isLoading, label, total]);

  // Callers are click handlers, and `load` records its own failures, so
  // there is nothing here to await or catch.
  /* eslint-disable @typescript-eslint/no-floating-promises */
  const loadMore = React.useCallback(() => { load('next'); }, [load]);
  const loadAll = React.useCallback(() => { load('all'); }, [load]);
  /* eslint-enable @typescript-eslint/no-floating-promises */

  return {
    rows,
    total,
    hasMore: rows.length < total,
    isLoading,
    failed,
    loadMore,
    loadAll,
  };
};
